import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  TextField,
  CircularProgress,
  makeStyles,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Avatar,
  IconButton,
  Fade,
  Grow,
  Slide,
  InputAdornment,
  Chip,
  Divider,
  Switch,
  Tooltip,
  Menu,
  MenuItem,
  FormControlLabel,
  Tab,
  Tabs,
  CardActionArea,
  CardContent,
  alpha,
  withStyles,
} from "@material-ui/core";

import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  School as SchoolIcon,
  Assessment as AssessmentIcon,
  LibraryBooks as LibraryBooksIcon,
  Create as CreateIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  EmojiObjects as InsightIcon,
  Mic as PodcastIcon,
  Timeline as ProjectIcon,
  AllInclusive as ScienceIcon,
  Launch,
  Build as BuildIcon,
  CloudQueue as ProvidersIcon,
  HourglassEmpty,
  DonutLarge,
  Policy,
  School,
  Build,
  AccountTree,
} from "@material-ui/icons";
import { Description } from "@material-ui/icons";

import { SwitchTransition, CSSTransition } from "react-transition-group";
import Button from "../../../components/CustomButtons/Button";
import { ToggleButtonGroup } from "../../../components/ToggleButtonGroup";
import { ToggleButton } from "../../../components/ToggleButton";

import { toolboxConstants } from "../../../variables/toolboxConstants";
import { toolboxSolutions } from "../../../variables/toolboxSolutions";
// Import your API function
import { createSolutionGeneratorPlan } from "views/APIs/SolutionGeneratorUtility";
import { addPlanToProject } from "views/APIs/SolutionGeneratorUtility";
import { ResponseViewer } from "./ResponseViewer";
import WaveTitle from "./WaveTitle";
import MDEditor from "@uiw/react-md-editor";

const iconForCategory = (cat) => {
  if (/Governance|MLOps/i.test(cat))
    return <DonutLarge fontSize="small" style={{ marginRight: "4px" }} />;
  if (/Regulatory/i.test(cat))
    return <Policy fontSize="small" style={{ marginRight: "4px" }} />;
  if (/Audit/i.test(cat))
    return <AccountTree fontSize="small" style={{ marginRight: "4px" }} />;
  if (/Learning|Enablement/i.test(cat))
    return <School fontSize="small" style={{ marginRight: "4px" }} />;
  if (/Knowledge|Summarization/i.test(cat))
    return <Description fontSize="small" style={{ marginRight: "4px" }} />;
  return <Build fontSize="small" style={{ marginRight: "4px" }} />;
};

const getAllUseCases = (byCategory) =>
  Object.entries(byCategory).flatMap(([category, cases]) =>
    Object.entries(cases).map(([name, obj]) => ({
      category,
      name,
      description: obj.description,
      solution: obj.solution,
    }))
  );

const normalizeSolutions = (maybeCategorized) => {
  const looksCategorized =
    typeof maybeCategorized === "object" &&
    Object.values(maybeCategorized)[0] &&
    typeof Object.values(maybeCategorized)[0] === "object" &&
    !("description" in Object.values(maybeCategorized)[0]); // first layer is categories, not use cases

  if (looksCategorized) return maybeCategorized;

  // Legacy flat → wrap
  return { Uncategorized: maybeCategorized };
};

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: theme.spacing(3),
  },
  header: {
    fontWeight: 800,
    letterSpacing: 0.3,
    textAlign: "center",
    marginBottom: theme.spacing(2),
  },
  card: {
    height: "100%",
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.18)",
    background: "rgba(255,255,255,0.04)",
    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
    transition:
      "transform .2s ease, box-shadow .2s ease, border-color .2s ease",
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: "0 10px 22px rgba(0,0,0,0.15)",
      borderColor: "rgba(32,103,123,0.4)",
      borderWidth: 2,
    },
  },
  row: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  tinyChip: {
    height: 24,
    fontWeight: 700,
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.18)",
    color: "rgba(255,255,255,0.8)",
    background: "transparent",
    marginRight: theme.spacing(1),
  },
  title: {
    fontWeight: 900,
    lineHeight: 1.2,
    marginBottom: theme.spacing(1),
  },
  body: {
    opacity: 0.9,
  },
  leftActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  attachButton: {
    color: "#fff",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.08)",
    },
  },
  providersButton: {
    color: "#fff",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.08)",
    },
  },
  providersMenu: {
    "& .MuiPaper-root": {
      backgroundColor: "rgba(32, 103, 123, 0.95)",
      backdropFilter: "blur(15px)",
      border: "1px solid rgba(255, 255, 255, 0.15)",
      borderRadius: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
    "& .MuiMenuItem-root": {
      color: "#ffffff",
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.1)",
      },
    },
  },
  formControlLabel: {
    margin: 0,
    width: "100%",
    "& .MuiFormControlLabel-label": {
      color: "#ffffff",
      fontSize: "0.95rem",
    },
    "& .MuiSwitch-colorSecondary.Mui-checked": {
      color: "#ffffff",
    },
    "& .MuiSwitch-colorSecondary.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "rgba(255, 255, 255, 0.5)",
    },
    "& .MuiSwitch-track": {
      backgroundColor: "rgba(255, 255, 255, 0.3)",
    },
  },
  container: {
    padding: theme.spacing(4),
    position: "relative",
    overflow: "auto",
  },
  hero: {
    textAlign: "center",
    marginBottom: theme.spacing(6),
    position: "relative",
    zIndex: 2,
  },
  heroTitle: {
    fontSize: "2.5rem",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: theme.spacing(2),
    letterSpacing: 0.2,
    [theme.breakpoints.down("sm")]: {
      fontSize: "2rem",
    },
  },
  heroDescription: {
    fontSize: "1.125rem",
    color: "rgba(255, 255, 255, 0.95)",
    maxWidth: "80vw",
    margin: "0 auto",
    lineHeight: 1.6,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1rem",
    },
  },
  inputSection: {
    marginBottom: theme.spacing(6),
    display: "flex",
    justifyContent: "center",
  },
  inputCard: {
    background:
      "linear-gradient(145deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.12) 100%)",
    backdropFilter: "blur(15px)",
    border: "2px solid #20677b",
    borderRadius: theme.spacing(3),
    padding: theme.spacing(3),
    width: "100%",
    position: "relative",
    animation: "$glassFloat 3s ease-in-out infinite",
    "&::before": {
      content: '""',
      position: "absolute",
      top: -2,
      left: -2,
      right: -2,
      bottom: -2,
      borderRadius: theme.spacing(3),
      background: "linear-gradient(45deg, #20677b, #165d64, #20677b)",
      zIndex: -1,
      animation: "$borderGlow 2s linear infinite",
    },
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      color: "#ffffff",
      "& fieldset": {
        borderColor: "transparent",
      },
      "&:hover fieldset": {
        borderColor: "transparent",
      },
      "&.Mui-focused fieldset": {
        borderColor: "transparent",
      },
    },
    "& .MuiInputLabel-root": {
      color: "rgba(255, 255, 255, 0.7)",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#ffffff",
    },
  },
  sendButton: {
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    color: "#ffffff",
    border: "1px solid rgba(255, 255, 255, 0.25)",
    backdropFilter: "blur(10px)",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1, 3),
    marginTop: theme.spacing(2),
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.25)",
      transform: "translateY(-2px)",
    },
    "&:disabled": {
      backgroundColor: "rgba(255, 255, 255, 0.08)",
      color: "rgba(255, 255, 255, 0.5)",
    },
  },
  attachButton: {
    color: "rgba(255, 255, 255, 0.7)",
    "&:hover": {
      color: "#ffffff",
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },
  actionsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 8,
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6),
  },
  loadingSpinner: {
    marginBottom: theme.spacing(2),
  },

  toolboxGrid: {
    marginTop: theme.spacing(3),
  },
  toolboxCard: {
    background:
      "linear-gradient(145deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.12) 100%)",
    backdropFilter: "blur(15px)",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    borderRadius: theme.spacing(2),
    padding: theme.spacing(2),
    height: "180px",
    cursor: "pointer",
    transition: "all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    position: "relative",
    color: "#ffffff",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 15px 30px rgba(0, 0, 0, 0.25)",
      background:
        "linear-gradient(145deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.18) 100%)",
      border: "1px solid rgba(255, 255, 255, 0.25)",
    },
  },
  componentCard: {
    background:
      "linear-gradient(145deg, rgba(32, 103, 123, 0.75) 0%, rgba(32, 103, 123, 0.92) 100%)",
    backdropFilter: "blur(15px)",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    borderRadius: theme.spacing(2),
    padding: theme.spacing(3),
    height: "200px",
    cursor: "pointer",
    transition: "all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    position: "relative",
    color: "#ffffff",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 15px 30px rgba(0, 0, 0, 0.25)",
      background:
        "linear-gradient(145deg, rgba(32, 103, 123, 0.65) 0%, rgba(32, 103, 123, 0.98) 100%)",
      border: "1px solid rgba(255, 255, 255, 0.25)",
      "& $launchButton": {
        opacity: 1,
        transform: "translateY(0)",
      },
    },
  },
  componentIcon: {
    width: 60,
    height: 60,
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    color: "#ffffff",
    marginBottom: theme.spacing(2),
    fontSize: "2rem",
    border: "1px solid rgba(255, 255, 255, 0.2)",
  },
  componentTitle: {
    fontSize: "1.125rem",
    fontWeight: 600,
    color: "#ffffff",
    marginBottom: theme.spacing(1),
    letterSpacing: 0.1,
  },
  componentDescription: {
    fontSize: "0.875rem",
    color: "rgba(255, 255, 255, 0.9)",
    lineHeight: 1.4,
  },
  launchButton: {
    position: "absolute",
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    opacity: 0,
    transform: "translateY(10px)",
    transition: "all 0.3s ease",
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    color: "#ffffff",
    border: "1px solid rgba(255, 255, 255, 0.25)",
    backdropFilter: "blur(10px)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.25)",
    },
  },
  toolboxIcon: {
    width: 50,
    height: 50,
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    color: "#ffffff",
    marginBottom: theme.spacing(1.5),
    fontSize: "1.5rem",
    border: "1px solid rgba(255, 255, 255, 0.2)",
  },
  toolboxTitle: {
    fontSize: "1rem",
    fontWeight: 600,
    color: "#ffffff",
    marginBottom: theme.spacing(1),
    letterSpacing: 0.1,
  },
  toolboxDescription: {
    fontSize: "0.75rem",
    color: "rgba(255, 255, 255, 0.9)",
    lineHeight: 1.4,
  },
  hiddenInput: {
    display: "none",
  },
  "@keyframes glassFloat": {
    "0%, 100%": {
      transform: "translateY(0px)",
    },
    "50%": {
      transform: "translateY(-5px)",
    },
  },
  "@keyframes borderGlow": {
    "0%": {
      backgroundPosition: "0% 50%",
    },
    "50%": {
      backgroundPosition: "100% 50%",
    },
    "100%": {
      backgroundPosition: "0% 50%",
    },
  },
  providersButton: {
    color: "rgba(255, 255, 255, 0.7)",
    "&:hover": {
      color: "#ffffff",
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },
  toggleGroup: {
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    borderRadius: theme.spacing(1),
  },
  toggleButton: {
    color: "rgba(255, 255, 255, 0.7)",
    border: "none",
    padding: theme.spacing(0.75, 2),
    "&.Mui-selected": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      color: "#ffffff",
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.25)",
      },
    },
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },
  selectedToggle: {
    backgroundColor: "rgba(255, 255, 255, 0.42)",
    color: "#ffffff",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.45)",
    },
  },
  stepLabelText: {
    color: "rgba(255, 255, 255, 0.9)",
  },
  stepDescription: {
    color: "rgba(234, 246, 249, 0.9)",
  },
  realCard: {
    borderRadius: 12,
    marginBottom: 12,
    background: "rgba(255,255,255,0.14)",
    color: "#EAF6F9",
  },
  flipScene: {
    perspective: 1200,
  },

  flipCard: {
    position: "relative",
    transformStyle: "preserve-3d",
    transition: "transform 700ms cubic-bezier(.2,.8,.2,1)",
    // Ensure there's room for the absolutely-positioned faces
    minHeight: 260, // adjust if your card is taller
    borderRadius: 16,
  },

  flipped: {
    transform: "rotateY(180deg)",
  },

  flipFace: {
    position: "absolute",
    inset: 0,
    backfaceVisibility: "hidden",
    WebkitBackfaceVisibility: "hidden",
    borderRadius: "inherit",
  },

  front: {
    transform: "rotateY(0deg)",
  },

  back: {
    transform: "rotateY(180deg)",
  },

  /* Optional: respect reduced motion */
  "@media (prefers-reduced-motion: reduce)": {
    flipCard: { transition: "none" },
  },

  chipsWrap: {
    marginTop: 8,
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  pills: {
    textTransform: "none",
    fontWeight: 700,
    mr: 1,
    minHeight: 40,
    borderRadius: 999,
    px: 2,
  },
}));

// -------- style helpers (v4) --------
const PillTabs = withStyles({
  root: {
    minHeight: 44,
    marginBottom: 16,
  },
  indicator: {
    display: "none", // hide the default underline
  },
})(Tabs);

const PillTab = withStyles((theme) => ({
  root: {
    textTransform: "none",
    fontWeight: 700,
    minHeight: 40,
    borderRadius: 10,
    padding: "8px 16px",
    marginRight: 8,
    border: "1px solid rgba(255,255,255,0.25)",
    background: "#20677b",
    color: "rgba(255,255,255,0.8)",
    "&:hover": {
      background: "rgba(23, 75, 90, 1)",
      boxShadow: "0 0 8px rgba(32,103,123,0.6)",
    },
    "&$selected": {
      color: "#fff",
      borderColor: "rgba(255,255,255,0.35)",
      background:
        "linear-gradient(90deg, rgba(11,73,90,0.95) 0%, rgba(32,103,123,0.9) 100%)",
    },
  },
  selected: {},
  wrapper: {
    flexDirection: "row",
    gap: 8,
  },
}))(Tab);

const SolutionGeneratorPage = () => {
  const classes = useStyles();
  const [useCase, setUseCase] = useState("");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [acceleratorMode, setAcceleratorMode] = useState("solution");
  const [providersAnchorEl, setProvidersAnchorEl] = useState(null);
  const [selectedProviders, setSelectedProviders] = useState({
    openai: true,
    gemini: true,
    claude: true,
    langchain: false,
    huggingface: false,
    cohere: false,
  });

  // response related
  const [activeStep, setActiveStep] = React.useState(0);
  const [tab, setTab] = useState(0);

  const isFlipped = acceleratorMode === "content";

  const solutionsByCategory = useMemo(
    () => normalizeSolutions(toolboxSolutions),
    []
  );

  const categories = Object.keys(solutionsByCategory);
  const [selectedCategory, setSelectedCategory] = useState(categories[0]);
  const useCasesInSelected = selectedCategory
    ? Object.entries(solutionsByCategory[selectedCategory]).map(
        ([name, obj]) => ({
          name,
          ...obj,
        })
      )
    : [];

  const handleNext = () =>
    setActiveStep((s) => Math.min(s + 1, (response?.steps?.length ?? 1) - 1));
  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  // Add these handler functions
  const handleProvidersClick = (event) => {
    setProvidersAnchorEl(event.currentTarget);
  };

  const handleProvidersClose = () => {
    setProvidersAnchorEl(null);
  };

  const handleProviderToggle = (provider) => {
    setSelectedProviders((prev) => ({
      ...prev,
      [provider]: !prev[provider],
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (!useCase.trim()) return;
    const formData = new FormData();
    formData.append("user_query", useCase);
    for (const file of attachedFiles) {
      formData.append("files", file, file.name);
    }

    // Normalize + flatten
    const solutionsByCategory = normalizeSolutions(toolboxSolutions);
    const allUseCases = getAllUseCases(solutionsByCategory);

    // Prefer exact name match first, then description contains
    const lcQuery = useCase.toLowerCase();

    const exactByName =
      allUseCases.find((uc) => uc.name.toLowerCase() === lcQuery) || null;

    const matchingSolution =
      exactByName ||
      allUseCases.find((uc) =>
        (uc.description || "").toLowerCase().includes(lcQuery)
      );

    if (matchingSolution) {
      setResponse(matchingSolution.solution);
      // wait 1 second
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setLoading(false);
      return;
    }

    try {
      const response = await createSolutionGeneratorPlan(formData);
      console.log("Generated solution response:", response);

      // Validate if response follows the ResponseModel structure
      const isValidResponse =
        response && response?.plan && Array.isArray(response?.plan?.steps);

      if (!isValidResponse) {
        setResponse({
          plan:
            response.plan.message ||
            "You seem to have a very specific request that might not be covered in our current solution library. Please try rephrasing your request or providing more details. Alternatively, reach out to info@qusandbox.com for personalized assistance.",
          error: "Failed to parse response - invalid response structure",
          reference_files: response.reference_files,
        });
      } else {
        setResponse(response);
      }
    } catch (error) {
      console.error("Error generating solution:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChipClick = (useCaseObjOrName, fromCard = false) => {
    // If called with a full object
    if (fromCard && useCaseObjOrName?.description) {
      setUseCase(useCaseObjOrName.description);
      return;
    }
    // If called with a string name (category chip no longer sets useCase)
    setUseCase(useCaseObjOrName);
  };

  const handleFileAttach = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileDelete = (name) => {
    setAttachedFiles((prev) => prev.filter((f) => f.name !== name));
  };

  // whenever the response changes, update the project with the plan
  useEffect(() => {
    if (response && response?.steps?.[0]?._id) {
      const formDataforPlan = new FormData();
      formDataforPlan.append("project_id", response?.steps[0]?._id);
      formDataforPlan.append("plan", JSON.stringify(response));
      addPlanToProject(formDataforPlan);
    }
  }, [response]);

  // update your existing handleFileChange to allow re-selecting same file again
  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    setAttachedFiles((prev) => Array.from([...prev, ...files]));
    // reset input so the same file can be picked again
    event.target.value = "";
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && event.ctrlKey) {
      handleSubmit();
    }
  };

  const angle = acceleratorMode === "solution" ? 180 : 0;
  // choose which animation to run based on direction
  const anim = acceleratorMode === "solution" ? "flipTo180" : "flipTo0";

  return (
    <Box>
      <Divider style={{ marginBottom: 16 }} />
      <Typography variant="h4">
        
        <WaveTitle
          text={
            acceleratorMode === "solution"
              ? "Solution Accelerator"
              : "Content Creator"
          }
          triggerKey={acceleratorMode.length}
        />
      </Typography>
      <Typography variant="body1">
        {acceleratorMode === "solution"
          ? "Describe your business challenge or use case, and our AI will generate a comprehensive solution with step-by-step guidance and recommended toolboxes."
          : "Describe your desired content idea, topic, or goal, and our AI will help you generate engaging content drafts with step-by-step guidance."}
      </Typography>
      <Divider style={{ marginTop: 16, marginBottom: 16 }} />
      <Box className={classes.container}>
        {/* input section */}
        <style>
          {`
                            @keyframes flipTo180 {
                            0%   { transform: rotateX(0deg); }
                            70%  { transform: rotateX(202deg); }  
                            100% { transform: rotateX(180deg); }
                            }

                            @keyframes flipTo0 {
                            0%   { transform: rotateX(180deg); }
                            70%  { transform: rotateX(-22deg); }  
                            100% { transform: rotateX(0deg); }
                            }
                        `}
        </style>
        <div style={{ perspective: 1000 }}>
          {/* FLIPPER */}
          <div
            style={{
              transform: `rotateX(${angle}deg)`,
              transformStyle: "preserve-3d",
              transformOrigin: "50% 50%",
              animation: `${anim} 650ms ease-out both`, // <-- bounce flip
              willChange: "transform",
            }}
          >
            {/* KEEP CONTENT UPRIGHT */}
            <div
              style={{
                transform: `rotateX(${-angle}deg)`,
                transformStyle: "preserve-3d",
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
              }}
            >
              <Slide
                direction="up"
                in={true}
                appear={false}
                mountOnEnter
                unmountOnExit={false}
                timeout={600}
              >
                <Card className={classes.inputCard} elevation={0}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    label={
                      acceleratorMode === "solution"
                        ? "Describe your business challenge or use case..."
                        : "Describe your desired content idea, topic, or goal..."
                    }
                    value={useCase}
                    onChange={(e) => setUseCase(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className={classes.textField}
                    disabled={loading}
                  />

                  {/* Hidden input for file picker */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className={classes.hiddenInput}
                    onChange={handleFileChange}
                    accept=".pdf,application/pdf"
                  />

                  {/* Row BELOW the textbox: attach on the left, generate on the right */}
                  <Box className={classes.actionsRow}>
                    <Box className={classes.leftActions}>
                      <ToggleButtonGroup
                        value={acceleratorMode}
                        exclusive
                        onChange={(event, newMode) => {
                          // Only change mode for content/solution buttons
                          if (newMode === "content" || newMode === "solution") {
                            setAcceleratorMode(newMode);
                          }
                        }}
                        disabled={loading}
                      >
                        <Tooltip title="Attach files">
                          <span>
                            <ToggleButton
                              value="attach"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFileAttach();
                              }}
                              aria-label="Attach files"
                            >
                              <AttachFileIcon style={{ color: "#f2f2f2" }} />
                            </ToggleButton>
                          </span>
                        </Tooltip>
                      </ToggleButtonGroup>
                    </Box>

                    <Button
                      variant="contained"
                      className={classes.sendButton}
                      startIcon={loading ? <HourglassEmpty /> : <SendIcon />}
                      onClick={handleSubmit}
                      disabled={loading || !useCase.trim()}
                    >
                      {loading
                        ? "Generating..."
                        : acceleratorMode === "solution"
                        ? "Generate Solution"
                        : "Generate Content"}
                    </Button>
                  </Box>

                  <Menu
                    anchorEl={providersAnchorEl}
                    open={Boolean(providersAnchorEl)}
                    onClose={handleProvidersClose}
                    className={classes.providersMenu}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    getContentAnchorEl={null}
                  >
                    <MenuItem
                      className={classes.providerMenuItem}
                      style={{
                        borderBottom: "1px solid rgba(255,255,255,0.25)",
                      }}
                      disableRipple
                    >
                      <Typography variant="body2">Choose Providers</Typography>
                    </MenuItem>
                    {Object.keys(selectedProviders).map((provider) => (
                      <MenuItem
                        key={provider}
                        className={classes.providerMenuItem}
                        disableRipple
                      >
                        <FormControlLabel
                          className={classes.formControlLabel}
                          control={
                            <Switch
                              checked={selectedProviders[provider]}
                              onChange={() => handleProviderToggle(provider)}
                              color="default"
                            />
                          }
                          label={
                            provider.charAt(0).toUpperCase() + provider.slice(1)
                          }
                        />
                      </MenuItem>
                    ))}
                  </Menu>

                  {/* File chips with delete “x” */}
                  {attachedFiles.length > 0 && (
                    <Box className={classes.chipsWrap}>
                      {attachedFiles.map((f) => (
                        <Chip
                          key={f.name + f.size}
                          label={f.name}
                          onDelete={() => handleFileDelete(f.name)}
                          variant="default"
                          style={{
                            color: "#fff",
                            border: "1px solid rgba(255,255,255,0.25)",
                            background: "rgba(255,255,255,0.12)",
                            margin: "4px",
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Card>
              </Slide>
            </div>
          </div>
        </div>

        {/* loading */}
        {loading && (
          <Fade in={loading}>
            <div className={classes.loadingContainer}>
              <CircularProgress size={48} className={classes.loadingSpinner} />
              <Typography variant="h6" style={{ marginTop: 16 }}>
                Generating solution...
              </Typography>
              <Typography
                variant="body2"
                style={{ marginTop: 8, opacity: 0.8 }}
              >
                Analyzing your use case and creating tailored recommendations
              </Typography>
            </div>
          </Fade>
        )}

        {!loading && response && (
          <ResponseViewer
            response={response}
            loading={loading}
            setResponse={setResponse}
          />
        )}

        {!response && !loading && (
          <>
            <Box className={classes.root}>
              <Typography variant="body1" className={classes.header}>
                Popular use cases
              </Typography>

              {/* Category Pills (v4 scrollable Tabs) */}
              <Box display="flex" justifyContent="center" width="100%">
                <PillTabs
                  value={tab}
                  onChange={(e, v) => {
                    setTab(v);
                    setSelectedCategory(categories[v]);
                  }}
                  variant="scrollable"
                  scrollButtons="auto"
                  aria-label="Use case categories"
                >
                  {categories.map((cat) => (
                    <PillTab
                      key={cat}
                      icon={iconForCategory(cat)}
                      label={cat}
                    />
                  ))}
                </PillTabs>
              </Box>

              {/* Cards */}
              <Grid container spacing={2}>
                {useCasesInSelected.map(({ name, description, solution }) => (
                  <Grid item xs={12}>
                    <Card className={classes.card} variant="outlined">
                      <CardActionArea
                        onClick={() =>
                          handleChipClick({ name, description, solution }, true)
                        }
                        style={{ height: "100%" }}
                      >
                        <CardContent>
                          <Typography variant="body1" className={classes.title}>
                            {name}
                          </Typography>

                          <Typography variant="body2" className={classes.body}>
                            {description}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default SolutionGeneratorPage;
